import { createSlice } from '@reduxjs/toolkit';

/**
 * Redux slice for Super Admin state management.
 *
 * Responsibilities:
 * - Store all tenants list for super admin tenant selector
 * - Track currently managing tenant (for cross-tenant management)
 * - Handle loading and error states
 *
 * Single Responsibility: Only handles super admin-specific state,
 * separate from the regular tenant slice which handles user's own tenant.
 */

// Helper function to get managing tenant from localStorage
const getManagingTenantFromLocalStorage = () => {
  try {
    const managingTenant = localStorage.getItem('managingTenant');
    return managingTenant ? JSON.parse(managingTenant) : null;
  } catch (_error) {
    // Error handled silently
    return null;
  }
};

// Helper function to save managing tenant to localStorage
const saveManagingTenantToLocalStorage = (tenant) => {
  try {
    if (tenant) {
      localStorage.setItem('managingTenant', JSON.stringify(tenant));
    } else {
      localStorage.removeItem('managingTenant');
    }
  } catch (_error) {
    // Error handled silently
  }
};

const initialState = {
  // All tenants for super admin dropdown
  allTenants: [],
  // Currently selected tenant to manage (for super admin cross-tenant management)
  managingTenant: getManagingTenantFromLocalStorage(),
  // Loading state for fetching tenants
  loadingTenants: false,
  // Error state
  error: null
};

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    /**
     * Set all tenants list (for super admin tenant selector)
     */
    setAllTenants: (state, action) => {
      state.allTenants = action.payload;
      state.error = null;
    },

    /**
     * Set the tenant super admin wants to manage
     * Persists to localStorage for session continuity
     */
    setManagingTenant: (state, action) => {
      state.managingTenant = action.payload;
      saveManagingTenantToLocalStorage(action.payload);
      state.error = null;
    },

    /**
     * Clear managing tenant selection
     */
    clearManagingTenant: (state) => {
      state.managingTenant = null;
      localStorage.removeItem('managingTenant');
    },

    /**
     * Set loading state for tenant fetch operations
     */
    setLoadingTenants: (state, action) => {
      state.loadingTenants = action.payload;
    },

    /**
     * Set error state
     */
    setAdminError: (state, action) => {
      state.error = action.payload;
      state.loadingTenants = false;
    },

    /**
     * Clear all admin state (on logout)
     */
    clearAdminState: (state) => {
      state.allTenants = [];
      state.managingTenant = null;
      state.loadingTenants = false;
      state.error = null;
      localStorage.removeItem('managingTenant');
    },

    /**
     * Restore managing tenant from localStorage (on app load)
     */
    restoreManagingTenant: (state) => {
      state.managingTenant = getManagingTenantFromLocalStorage();
    }
  }
});

export const {
  setAllTenants,
  setManagingTenant,
  clearManagingTenant,
  setLoadingTenants,
  setAdminError,
  clearAdminState,
  restoreManagingTenant
} = adminSlice.actions;

export default adminSlice.reducer;
